import { useQuery } from '@tanstack/react-query';
import http from 'helpers/requests';

/**
 * The two incoming-request endpoints, each behind ONE react-query cache entry
 * shared by every consumer (the Requests page lists, the sidebar badge, the
 * Overview pending-requests panel).
 *
 * Before this, the count hook keyed on the caller's *user type*
 * (`['operatorInvites','operator']`) while the list hook keyed on the *mode
 * label* (`['operatorInvites','Declarant Requests']`), so the very same two
 * GETs ran under three different keys — 4 requests a minute on the Requests
 * page where 2 suffice. Keys and options now live here so react-query can
 * actually dedupe: observers only share a poll timer when they agree on the
 * key AND on `refetchInterval`/`staleTime`.
 */
export const INVITE_KINDS = Object.freeze({
  DECLARANT: 'declarant',
  OPERATOR: 'operator',
});

const INVITE_ENDPOINTS = {
  [INVITE_KINDS.DECLARANT]: '/requests',
  [INVITE_KINDS.OPERATOR]: '/requests/operator',
};

/** Cache key for one endpoint. `['operatorInvites']` stays the invalidation prefix. */
export const operatorInviteQueryKey = (kind) => ['operatorInvites', kind];

/**
 * Canonical query options. Every consumer must build its `useQuery` from this
 * so the shared cache entry has one set of poll settings.
 */
export const operatorInviteQueryOptions = (kind, { enabled = true } = {}) => ({
  queryKey: operatorInviteQueryKey(kind),
  queryFn: async () => {
    const response = await http.get(INVITE_ENDPOINTS[kind]);
    return Array.isArray(response.data) ? response.data : [];
  },
  enabled,
  staleTime: 30000, // 30 seconds
  refetchInterval: 60000, // Refetch every minute for real-time updates
});

/**
 * Incoming requests for one side of the split.
 *
 * @param {string} mode     Active mode label.
 * @param {string[]} modes  `[declarantLabel, operatorLabel]` — index 0 is the declarant side.
 */
export const useFetchOperatorInvites = (mode, modes) => {
  const kind = mode === modes?.[0] ? INVITE_KINDS.DECLARANT : INVITE_KINDS.OPERATOR;
  return useQuery(operatorInviteQueryOptions(kind));
};
